// Image export (CONTRACTS §10): rasterize a chosen section to PNG (with a
// background or transparent) and serialize inline SVGs as standalone .svg files.
// The doc iframe is same-origin, so the parent reads its nodes directly. Every
// export carries the CONFIDENTIAL marking.

import { toPng } from "html-to-image";
import { CONFIDENTIAL_LABEL } from "./labels";

export type ExportBackground = "transparent" | "white" | "cream";

const BG: Record<ExportBackground, string | undefined> = {
  transparent: undefined,
  white: "#ffffff",
  cream: "#f2efdf",
};

function saveDataUrl(dataUrl: string, filename: string): void {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function safeName(name: string): string {
  return (name || "export").replace(/[^\w.-]+/g, "-").replace(/^-+|-+$/g, "");
}

/** All inline SVGs in a (same-origin) document, in document order. */
export function listSvgs(doc: Document): SVGSVGElement[] {
  return Array.from(doc.querySelectorAll("svg"));
}

/** Stamp the CONFIDENTIAL label into the top-right corner of a PNG data URL. */
function stampConfidential(dataUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.drawImage(img, 0, 0);
      const pad = Math.max(8, Math.round(canvas.width * 0.012));
      const size = Math.max(10, Math.round(canvas.width * 0.014));
      ctx.font = `700 ${size}px -apple-system, "Helvetica Neue", Arial, sans-serif`;
      ctx.textAlign = "right";
      ctx.textBaseline = "top";
      ctx.fillStyle = "rgba(184, 66, 57, 0.92)";
      ctx.fillText(CONFIDENTIAL_LABEL, canvas.width - pad, pad);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/** Rasterize a DOM node to a stamped PNG and download it. */
export async function exportNodePng(
  node: HTMLElement | SVGElement,
  opts: { background: ExportBackground; scale?: number; name: string }
): Promise<void> {
  const dataUrl = await toPng(node as HTMLElement, {
    backgroundColor: BG[opts.background],
    pixelRatio: opts.scale ?? 2,
    cacheBust: true,
  });
  const stamped = await stampConfidential(dataUrl);
  saveDataUrl(stamped, `${safeName(opts.name)}.png`);
}

/** Serialize an inline SVG to a standalone, stamped .svg file and download it. */
export function exportSvg(svg: SVGSVGElement, name: string): void {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  if (!clone.getAttribute("xmlns")) {
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  }
  // Small confidential marking in the corner of the vector.
  const NS = "http://www.w3.org/2000/svg";
  const text = document.createElementNS(NS, "text");
  text.setAttribute("x", "4");
  text.setAttribute("y", "12");
  text.setAttribute("font-family", "Arial, sans-serif");
  text.setAttribute("font-size", "9");
  text.setAttribute("font-weight", "700");
  text.setAttribute("fill", "#b84239");
  text.setAttribute("opacity", "0.85");
  text.textContent = CONFIDENTIAL_LABEL;
  clone.appendChild(text);

  const xml = new XMLSerializer().serializeToString(clone);
  const blob = new Blob(['<?xml version="1.0" encoding="UTF-8"?>\n', xml], {
    type: "image/svg+xml",
  });
  const url = URL.createObjectURL(blob);
  saveDataUrl(url, `${safeName(name)}.svg`);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
